/**
 * @module modules/useLoading
 * @description React hook for tracking loading state of promises.
 * Provides automatic loading state management for async operations.
 */

import React from 'react';

/**
 * @callback UseLoadingHook
 * @param {boolean} isLoading is promise loading?
 * @param {load} load hook to load promise (async)
 */

/**
 * @func useLoading
 * @summary React Hook to load a Promise (essentially converts a Promise to State)
 * @returns {Parameters<UseLoadingHook>} is loading(?) + hook to load promises
 */
function useLoading() {
  const [isLoading, setState] = React.useState(false);

  /**
   * @function load
   * @description Wrap a promise with loading state management. Sets loading to true before execution,
   * false after completion. Preserves promise chain for further handling.
   * @param {Promise<*>} aPromise - Promise to wrap with loading state
   * @returns {Promise<*>} Original promise that resolves/rejects with same values
   */
  const load = (aPromise) => {
    setState(true);
    return aPromise
      .then((...args) => {
        setState(false);
        return Promise.resolve(...args);
      })
      .catch((...args) => {
        setState(false);
        return Promise.reject(...args); // eslint-disable-line prefer-promise-reject-errors
      });
  };
  return [isLoading, load];
}

export default useLoading;
